import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { ConfirmModal } from '../../ui/ConfirmModal';
import { getExternalReleases, createExternalRelease, updateExternalRelease, deleteExternalRelease, getArtists } from '../../../services/api';
import { useToast } from '../../../hooks/use-toast';
import { Plus, Edit, Trash2, ExternalLink, X, Upload, Link as LinkIcon } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ExternalReleasesManager = () => {
  const { toast } = useToast();
  const [releases, setReleases] = useState([]);
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRelease, setEditingRelease] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, release: null });
  const [formData, setFormData] = useState({
    title: '',
    artists: [],
    externalUrl: '',
    artwork: '',
    releaseDate: '',
    type: 'EP'
  });
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState('');

  const fetchReleases = useCallback(async () => {
    try {
      const response = await getExternalReleases();
      setReleases(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch external releases', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const fetchArtists = useCallback(async () => {
    try {
      const response = await getArtists();
      setArtists(response.data);
    } catch (error) {
      console.error('Failed to fetch artists:', error);
    }
  }, []);

  useEffect(() => {
    fetchReleases();
    fetchArtists();
  }, [fetchReleases, fetchArtists]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.artists || formData.artists.length === 0) {
      toast({ title: 'Error', description: 'Please select at least one artist', variant: 'destructive' });
      return;
    }
    
    try {
      if (editingRelease) {
        await updateExternalRelease(editingRelease.id, formData);
        toast({ title: 'Success', description: 'External release updated successfully' });
      } else {
        await createExternalRelease(formData);
        toast({ title: 'Success', description: 'External release created successfully' });
      }
      setIsDialogOpen(false);
      resetForm();
      fetchReleases();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save external release', variant: 'destructive' });
    }
  };

  const handleEdit = (release) => {
    setEditingRelease(release);
    setFormData({
      title: release.title,
      artists: release.artists || [],
      externalUrl: release.externalUrl,
      artwork: release.artwork || '',
      releaseDate: release.releaseDate || '',
      type: release.type
    });
    setImagePreview(release.artwork || '');
    setIsDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await deleteExternalRelease(deleteModal.release.id);
      toast({ title: 'Success', description: 'External release deleted successfully' });
      fetchReleases();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete external release', variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({ title: '', artists: [], externalUrl: '', artwork: '', releaseDate: '', type: 'EP' });
    setEditingRelease(null);
    setImagePreview('');
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (e.target.name === 'artwork') {
      setImagePreview(e.target.value);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    setUploading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API_URL}/api/upload`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      });
      const imageUrl = response.data.url;
      setFormData({ ...formData, artwork: imageUrl });
      setImagePreview(imageUrl);
      toast({ title: 'Success', description: 'Image uploaded successfully' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to upload image', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const handleArtistToggle = (artistName) => {
    let updatedArtists;
    if (formData.artists.includes(artistName)) {
      updatedArtists = formData.artists.filter(a => a !== artistName);
    } else {
      updatedArtists = [...formData.artists, artistName];
    }
    setFormData({ ...formData, artists: updatedArtists });
  };

  const removeArtist = (artistName) => {
    const updatedArtists = formData.artists.filter(a => a !== artistName);
    setFormData({ ...formData, artists: updatedArtists });
  };

  if (loading) {
    return <div className="text-center py-20 text-gray-400">Loading external releases...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white">External Releases</h2>
          <p className="text-gray-400 text-sm mt-1">Manage releases on other platforms</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} className="bg-gradient-to-r from-orange-500 to-green-500 hover:from-orange-600 hover:to-green-600">
              <Plus className="w-4 h-4 mr-2" /> Add External Release
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-black border-green-500/30 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">
                {editingRelease ? 'Edit External Release' : 'Add External Release'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Title *</label>
                <Input 
                  name="title" 
                  value={formData.title} 
                  onChange={handleChange} 
                  required 
                  className="bg-black/50 border-green-500/30" 
                />
              </div>

              {/* Multi-Artist Selection */}
              <div>
                <label className="block text-sm font-medium mb-2">Artists *</label>
                <div className="bg-black/50 border border-green-500/30 rounded-md p-3 min-h-[50px]">
                  <div className="flex flex-wrap gap-2 mb-2">
                    {formData.artists.map((artistName) => (
                      <Badge 
                        key={artistName}
                        variant="secondary"
                        className="bg-orange-500/20 text-orange-300 border-orange-500/50 pr-1"
                      >
                        {artistName}
                        <button
                          type="button"
                          onClick={() => removeArtist(artistName)}
                          className="ml-2 hover:bg-orange-500/30 rounded-full p-0.5"
                        >
                          <X size={14} />
                        </button>
                      </Badge>
                    ))}
                  </div>
                  <Select onValueChange={handleArtistToggle}>
                    <SelectTrigger className="bg-black/30 border-green-500/20">
                      <SelectValue placeholder="Select artists..." />
                    </SelectTrigger>
                    <SelectContent className="bg-black border-green-500/30 text-white max-h-60">
                      {artists.map((artist) => (
                        <SelectItem key={artist.id} value={artist.name}>
                          <div className="flex items-center justify-between w-full">
                            <span>{artist.name}</span>
                            {formData.artists.includes(artist.name) && (
                              <Badge className="ml-2 bg-green-500/20 text-green-400">✓</Badge>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">External URL *</label>
                <Input 
                  name="externalUrl" 
                  value={formData.externalUrl} 
                  onChange={handleChange} 
                  required 
                  placeholder="https://spotify.com/album/..." 
                  className="bg-black/50 border-green-500/30" 
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Release Date (YYYY-MM)</label>
                <Input 
                  name="releaseDate" 
                  value={formData.releaseDate} 
                  onChange={handleChange} 
                  placeholder="2024-12" 
                  className="bg-black/50 border-green-500/30" 
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Type</label>
                <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                  <SelectTrigger className="bg-black/50 border-green-500/30">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-black border-green-500/30">
                    <SelectItem value="EP">EP</SelectItem>
                    <SelectItem value="Album">Album</SelectItem>
                    <SelectItem value="Single">Single</SelectItem>
                    <SelectItem value="Compilation">Compilation</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Artwork (Optional)</label>
                <Tabs defaultValue="url" className="w-full">
                  <TabsList className="grid w-full grid-cols-2 bg-black/50">
                    <TabsTrigger value="url"><LinkIcon className="w-4 h-4 mr-2" /> URL</TabsTrigger>
                    <TabsTrigger value="upload"><Upload className="w-4 h-4 mr-2" /> Upload</TabsTrigger>
                  </TabsList>
                  <TabsContent value="url">
                    <Input 
                      name="artwork" 
                      value={formData.artwork} 
                      onChange={handleChange} 
                      placeholder="https://example.com/artwork.jpg" 
                      className="bg-black/50 border-green-500/30" 
                    />
                  </TabsContent>
                  <TabsContent value="upload">
                    <Input 
                      type="file" 
                      accept="image/*" 
                      onChange={handleFileUpload} 
                      disabled={uploading} 
                      className="bg-black/50 border-green-500/30" 
                    />
                    {uploading && <p className="text-sm text-orange-400 mt-2">Uploading...</p>}
                  </TabsContent>
                </Tabs>
                {imagePreview && (
                  <img src={imagePreview} alt="Preview" className="mt-2 w-full h-48 object-cover rounded border border-green-500/30" />
                )}
              </div>

              <Button type="submit" disabled={uploading} className="w-full bg-gradient-to-r from-orange-500 to-green-500 hover:from-orange-600 hover:to-green-600">
                {editingRelease ? 'Update Release' : 'Create Release'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {releases.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <p>No external releases yet. Add your first one!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {releases.map((release) => (
            <Card key={release.id} className="bg-black/50 border-orange-500/30 hover:border-orange-500 hover:shadow-xl hover:shadow-orange-500/30 hover:-translate-y-1 overflow-hidden group p-0 transition-all duration-300">
              {release.artwork && (
                <div className="relative aspect-square overflow-hidden">
                  <img 
                    src={release.artwork} 
                    alt={release.title} 
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                  />
                  <div className="absolute top-3 right-3 z-10">
                    <span className="bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                      {release.type}
                    </span>
                  </div>
                </div>
              )}
              <div className="p-4 pb-6">
                <h3 className="text-lg font-bold text-white">{release.title}</h3>
                <p className="text-gray-400 text-sm">{release.artists.join(', ')}</p>
                {release.releaseDate && <p className="text-gray-500 text-xs mt-1">{release.releaseDate}</p>}
                <div className="flex gap-2 mt-4">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => handleEdit(release)} 
                    className="flex-1 border-green-500/30 hover:bg-green-500/20"
                  >
                    <Edit className="w-4 h-4 mr-1" /> Edit
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => setDeleteModal({ isOpen: true, release })} 
                    className="flex-1 border-red-500/30 hover:bg-red-500/20 text-red-400"
                  >
                    <Trash2 className="w-4 h-4 mr-1" /> Delete
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => window.open(release.externalUrl, '_blank')} 
                    className="border-orange-500/30 hover:bg-orange-500/20"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, release: null })}
        onConfirm={handleDelete}
        title="Delete External Release"
        message={`Are you sure you want to delete "${deleteModal.release?.title}"?`}
      />
    </div>
  );
};
